import React from 'react'
import { makeStyles, Theme } from '@material-ui/core/styles'
import { Grid, StepContent } from '@material-ui/core'
import Stepper from '@material-ui/core/Stepper'
import Step from '@material-ui/core/Step'
import StepLabel from '@material-ui/core/StepLabel'
import StepConnector from '@material-ui/core/StepConnector'
import UploadTemplate from '../../../../SemiConfigurators/components/UploadTemplate'
import { FormattedMessage, useIntl } from 'react-intl'
import messages from '../../../messages'
import { TermInput } from './Types'
import { semiConfigContext, ProductDetails } from '../Crisprgna'
import { useSearchTermLazyQuery } from '@src/queries/CrisprgrnaProductSearchQuery.generated'
import { ActiveLinkType, FileFormat } from '../Enum'
import { crisprgrnaRoute, useRouter } from '@src/routes'
import DownloadTemplate from '../../../../SemiConfigurators/components/DownloadTemplate'
import { initialState } from '../Reducer'
import ValidationErrors from '../../../../Configurators/validations/ValidationErrors'
import CautionPopUp from '@src/routes/SemiConfigurators/components/CautionPopUp'
import CircleLoader from '@src/components/CircleLoader/CircleLoader'
import { SemiConfiguratorValues } from '../../../utils/constants'
import PerformanceGuaranteePopUp from '@src/routes/SemiConfigurators/components/PopUp/PerformanceGuaranteePopUp'
import clsx from 'clsx'
import styles from '@src/routes/SemiConfigurators/utils/styles'

const useStyles = makeStyles((theme: Theme) => {
  return {
    contentWrapper: {
      backgroundColor: theme.palette.common.white,
      padding: theme.spacing(5, 5),
    },
    step: {
      color: theme.palette.primary.main,
      marginBottom: theme.spacing(6),
    },
    TempMarg: {
      marginLeft: theme.spacing(-86.2),
      marginBottom: theme.spacing(-23.5),
      paddingTop: theme.spacing(10),
    },
    iconText: {},
    icon: {
      fill: theme.palette.primary.main,
      borderWidth: 0,
      borderRadius: theme.spacing(6),
      '&$activeIcon': {
        fill: 'theme.palette.primary.main',
      },
    },
    activeIcon: {},
    stepperContainer: {
      padding: theme.spacing(3, 0),
      marginTop: theme.spacing(5),
    },
    label: {
      fontSize: theme.typography.pxToRem(14),
      marginBottom: theme.spacing(5),
      fontWeight: theme.typography.fontWeightRegular,
    },
    gridPadding: {
      marginTop: theme.spacing(0),
      marginBottom: theme.spacing(-9),
    },
    paddingStepOne: {
      paddingBottom: theme.spacing(4),
    },
    fieldLabel: {
      marginTop: theme.spacing(5),
      fontWeight: 'bold',
      fontSize: theme.typography.pxToRem(20),
      color: theme.palette.common.black,
      marginBottom: theme.spacing(5),
      marginLeft: theme.spacing(5),
      marginRight: theme.spacing(5),
    },
    stepLabel: {
      fontWeight: theme.typography.fontWeightRegular,
    },
    contentRoot: {
      borderColor: theme.palette.common.white,
    },
    uploadButton: {
      backgroundColor: theme.palette.primary.main,
      minWidth: 100,
      color: theme.palette.common.white,
      '&:disabled': {
        color: theme.palette.common.white,
      },
      marginTop: theme.spacing(2),
    },
    seperator: {
      textAlign: 'center',
    },
    divider: {
      margin: '10px auto',
      height: '50%',
    },
    resetConnector: {
      display: 'none',
    },
    stepLabelText: {
      fontWeight: theme.typography.fontWeightRegular,
      marginBottom: theme.spacing(0),
    },
    recommendationText: {
      position: 'relative',
      padding: theme.spacing(0, 0, 4.5, 0),
    },
  }
})

const downloadItems = {
  label: 'Download Template',
  isPCR: false,
  fileName: 'CRISPR_gRNA_Template.xlsx',
  productType: 'crisprgrna',
}

interface UploadQueriesProps {
  productDetails: ProductDetails
}

const UploadQueries: React.FC<UploadQueriesProps> = (props) => {
  const intl = useIntl()
  const classes = useStyles()
  const globalClasses = styles()
  const [getSearchTerm, { data, loading }] = useSearchTermLazyQuery()
  const { state, dispatch } = React.useContext(semiConfigContext)
  const router = useRouter()
  const value = { ...state.searchValue }
  const message =
    intl.formatMessage(messages.CONF_MANUAL_SEARCH_1_ERROR_POPUP_crispgrna) +
    `${value.term}` +
    intl.formatMessage(messages.CONF_MANUAL_SEARCH_2_ERROR_POPUP_crispgrna)
  const [checkCountError, setCheckCountError] = React.useState(false)
  const [upload, setUpload] = React.useState(false)
  const [validationMessage, setValidationMessage] = React.useState<string[]>([])
  const [dialogOpen, setDialogOpen] = React.useState(false)
  const [datavalue, setDatavalue] = React.useState<any>('')

  React.useEffect(() => {
    window.scrollTo(0, 0)
    setUpload(false)
    dispatch({ type: 'SET_PAGE_ERROR', data: '' })
    // TODO: need a test before we refactor these dependencies
  }, [])

  const searchResult = async (obj) => {
    await getSearchTerm({ variables: { input: obj } })
  }

  const parseSpreadsheet = async (file?: File): Promise<TermInput[]> =>
    new Promise((resolve, reject) => {
      if (!file) {
        return resolve([])
      }
      const reader = new FileReader()
      reader.onload = async (e) => {
        try {
          const XLSX = await import('xlsx')
          const data = new Uint8Array((e.target as any).result)
          const workbook = XLSX.read(data, { type: 'array' })
          const sheetName = workbook.SheetNames[0]
          const worksheet = workbook.Sheets[sheetName]
          const hasHeaders = worksheet['A4']?.v === FileFormat.HeaderName
          if (hasHeaders) {
            const products = (
              XLSX.utils.sheet_to_json(worksheet, {
                header: ['term'],
              }) as TermInput[]
            ).map((material: Record<keyof TermInput, any>) => {
              let { term } = material
              if (typeof term !== 'string') {
                term = term ? term.toString() : ''
              }
              return {
                term,
              }
            })
            products.splice(0, 1)
            resolve(products)
          } else {
            dispatch({
              type: 'SET_PAGE_ERROR',
              data: intl.formatMessage(messages.CONF_DNAPLATES_ERROR_STEP2),
            })
            setValidationMessage([
              intl.formatMessage(messages.CONF_INVALID_TEMPLATE),
            ])
          }
        } catch (err) {
          reject(err)
        }
      }
      reader.readAsArrayBuffer(file)
    })

  const onFileChange = async (file) => {
    dispatch({ type: 'SET_PAGE_ERROR', data: '' })
    setUpload(true)
    setCheckCountError(false)
    if (state.stepNo) {
      dispatch({ type: 'TRACE_ON_CLICK', data: true })
      dispatch({
        type: 'UPDATE_SEARCH_RESPONSE',
        data: initialState.searchResponseJson,
      })
      dispatch({
        type: 'UPDATE_DROPDOWN_RESPONSE',
        data: initialState.dropDownResponseList,
      })
      dispatch({
        type: 'UPDATE_PRICE_CALL_RESPONSE',
        data: initialState.priceCallResponse,
      })
      dispatch({
        type: 'UPDATE_DROP_DOWN',
        data: initialState.dropDownValue,
      })
      dispatch({
        type: 'UPDATE_PRICE_CALL',
        data: initialState.priceCallData,
      })
      dispatch({
        type: 'UPDATE_SPECIFICATION_VALUES',
        data: initialState.specificationValues,
      })
      dispatch({
        type: 'UPDATE_PAGINATION_DATA',
        data: initialState.paginationData,
      })
      dispatch({
        type: 'SET_STEP_NO',
        data: initialState.stepNo,
      })
      dispatch({
        type: 'SET_PAGE_ERROR',
        data: initialState.pageError,
      })
    }
    if (
      FileFormat.AcceptFormat &&
      FileFormat.AcceptFormat.split(',')
        .map((type) => type.trim().toLowerCase())
        .includes(file?.type)
    ) {
      let input: any = ''
      input = await parseSpreadsheet(file)
      let body = ''
      if (input && input.length > 100) {
        dispatch({ type: 'CHECK_FILE_UPLOAD', data: true })
        setCheckCountError(true)
        dispatch({
          type: 'SET_PAGE_ERROR',
          data: intl.formatMessage(messages.CONF_DNAPLATES_ERROR_STEP2),
        })
        setValidationMessage([
          intl.formatMessage(messages.CONF_UPLOAD_ERROR_MSG),
        ])
        window.scrollTo(0, 0)
      }
      if (!input.length) {
        dispatch({
          type: 'SET_PAGE_ERROR',
          data: intl.formatMessage(messages.CONF_DNAPLATES_ERROR_STEP2),
        })
        setValidationMessage([intl.formatMessage(messages.CONF_EMPTY_FILE)])
      }
      if (input && input.length) {
        input.forEach((item) => {
          body += `${item.term} `
        })
        body = body.trim()
      }

      if (body && input && input.length < 101) {
        setValidationMessage([])
        const requestData = {
          ...state.searchValue,
          term: body,
          locale: props.productDetails.locale,
          region: props.productDetails.region,
          page: 1,
          count: 10,
          store: props.productDetails.store,
        }
        dispatch({
          type: 'SET_PER_PAGE_COUNT',
          data: initialState.perPageCount,
        })
        dispatch({
          type: 'UPDATE_SEARCH_TERM',
          data: requestData,
        })
        searchResult(requestData)
      }
      if (!state.stepNo) {
        dispatch({ type: 'TRACE_ON_CLICK', data: false })
      }
    } else if (file !== undefined) {
      dispatch({
        type: 'SET_PAGE_ERROR',
        data: intl.formatMessage(messages.CONF_DNAPLATES_ERROR_STEP2),
      })
      setValidationMessage([intl.formatMessage(messages.CONF_INVALID_FORMAT)])
    }
  }

  const handleCautionPopup = (data) => {
    onFileChange(data)
  }

  const validateUploadFile = (file) => {
    if (state.searchResponseJson.reagents.length !== 0) {
      setDialogOpen(true)
      setDatavalue(file)
      dispatch({ type: 'SET_PAGINATION_PAGE', data: 1 })
    } else {
      setDialogOpen(false)
      onFileChange(file)
    }
  }

  React.useEffect(() => {
    if (data !== undefined) {
      if (!data.getSearchTerm?.serverError) {
        dispatch({ type: 'UPDATE_SEARCH_RESPONSE', data: data.getSearchTerm })
        const url = `${crisprgrnaRoute.index()}?activeLink=${
          ActiveLinkType.ItemSelection
        }`
        if (!data.getSearchTerm.count && state.searchValue.term !== '') {
          dispatch({ type: 'CHECK_FILE_UPLOAD', data: true })
          dispatch({
            type: 'SET_PAGE_ERROR',
            data: intl.formatMessage(messages.CONF_DNAPLATES_ERROR_STEP2),
          })
          setValidationMessage([message])
        }
        if (
          data !== undefined &&
          data.getSearchTerm.count &&
          !checkCountError
        ) {
          dispatch({ type: 'CHECK_FILE_UPLOAD', data: false })
          router.push(url)
          setUpload(false)
        }
      } else {
        dispatch({
          type: 'SET_PAGE_ERROR',
          data: SemiConfiguratorValues.InternalServerError,
        })
        window.scrollTo(0, 0)
      }
    }
    // TODO: need a test before we refactor these dependencies
  }, [data])

  return (
    <>
      <div className={classes.contentWrapper}>
        <Grid item xs={6} className={classes.recommendationText}>
          <PerformanceGuaranteePopUp />
        </Grid>
        <div>
          <FormattedMessage
            {...messages.CONF_OPTIONAL_INTR_MOD_TEMPLATE_standard}
          />
        </div>

        <Stepper
          orientation="vertical"
          classes={{ root: classes.stepperContainer }}
          connector={
            <StepConnector
              classes={{
                root: classes.resetConnector,
              }}
            />
          }
        >
          <Step active className={classes.step}>
            <StepLabel
              StepIconProps={{
                classes: {
                  root: classes.icon,
                  active: classes.activeIcon,
                  text: classes.iconText,
                },
              }}
            >
              <div className={classes.paddingStepOne}>
                <Grid
                  container
                  alignItems="center"
                  spacing={5}
                  className={classes.gridPadding}
                >
                  <Grid item sm={5}>
                    <div className={classes.label}>
                      <FormattedMessage
                        {...messages.CONF_MULTIPLE_ENTRY_STEP_1}
                      />
                    </div>
                  </Grid>
                  <Grid item sm={7}>
                    <div className={classes.label}>
                      <DownloadTemplate items={downloadItems} />
                    </div>
                  </Grid>
                </Grid>
              </div>
            </StepLabel>
          </Step>
          <Step active className={clsx(classes.step, globalClasses.mTop12)}>
            <StepLabel
              StepIconProps={{
                classes: {
                  root: classes.icon,
                  active: classes.activeIcon,
                  text: classes.iconText,
                },
              }}
            >
              <div className={classes.stepLabelText}>
                <FormattedMessage {...messages.CONF_MULTIPLE_ENTRY_STEP_2} />
              </div>
            </StepLabel>
          </Step>
          <Step active className={classes.step}>
            <StepLabel
              StepIconProps={{
                classes: {
                  root: classes.icon,
                  active: classes.activeIcon,
                  text: classes.iconText,
                },
              }}
            >
              <div className={classes.stepLabel}>
                <FormattedMessage {...messages.CONF_FILE_UPLOAD_crispgrna} />
              </div>
            </StepLabel>
            <StepContent
              classes={{
                root: classes.contentRoot,
              }}
            >
              <Grid container>
                <Grid item sm={3}>
                  <UploadTemplate
                    handleUploadChange={([file]) => validateUploadFile(file)}
                    dropText={intl.formatMessage(messages.DROP_CSV_XLS_FILE_OR)}
                    accept={FileFormat.AcceptFormat}
                  />
                  {upload && (
                    <div>
                      <ValidationErrors errors={validationMessage} />
                    </div>
                  )}
                </Grid>
              </Grid>
            </StepContent>
          </Step>
        </Stepper>
        <CautionPopUp
          dialogOpen={dialogOpen}
          setDialogOpen={setDialogOpen}
          handleCautionPopup={handleCautionPopup}
          data={datavalue}
        />
      </div>
      {loading && <CircleLoader />}
    </>
  )
}

export default UploadQueries
